<script setup lang="ts">
import type { CondoTalkToSpecialistProps } from '.';
import type { Agent } from '~/core/entities/agent/Agent';
import type { FunctionalComponent, SVGAttributes } from 'vue';

import { grammarInflection } from '~~/utils';

import IconMaterialSymbolsForumOutlineSharp from '~icons/material-symbols-light/forum-outline-sharp';
import IconMaterialSymbolsSupportAgentSharp from '~icons/material-symbols-light/support-agent-sharp';
import IconMaterialSymbolsVpnKeyOutline from '~icons/material-symbols-light/vpn-key-outline';
import IconMaterialSymbolsWorkspacePremiumOutline from '~icons/material-symbols-light/workspace-premium-outline';

import messages from './CondoTalkToSpecialist.locale.json';

const { t } = useI18n({
	useScope: 'local',
	messages
});

const props = withDefaults(defineProps<CondoTalkToSpecialistProps>(), {
	agents: () => [],
	condoName: ''
});

const agents = computed((): Agent[] => {
	return (props.agents || []).slice(0, 4);
});

const numberOfAgents = computed<number>(() => props.agents.length);
const agentsLabel = computed<string>(() => {
	if (!numberOfAgents.value) return 'Fale com um especialista';

	const label = grammarInflection(
		numberOfAgents.value,
		'corretor disponível',
		'corretores disponíveis'
	);

	return `${numberOfAgents.value} ${label}`;
});

const infos: {
	icon: FunctionalComponent<SVGAttributes, {}, any, {}>;
	label: string;
}[] = [
	{
		icon: IconMaterialSymbolsSupportAgentSharp,
		label: t('info.personalizedAttention')
	},
	{
		icon: IconMaterialSymbolsVpnKeyOutline,
		label: t('info.accessToOpportunities', { condoName: props.condoName })
	},
	{
		icon: IconMaterialSymbolsWorkspacePremiumOutline,
		label: t('info.highStandardMarket')
	},
	{
		icon: IconMaterialSymbolsForumOutlineSharp,
		label: t('info.directContact')
	}
];
</script>

<template>
	<div class="flex flex-col">
		<div
			class="flex flex-col items-start gap-6 self-stretch bg-secondary px-5 py-6 pb-8 md:px-8 md:pb-11 md:pt-8"
		>
			<div class="flex flex-col items-start gap-6 self-stretch">
				<div class="flex flex-col items-start gap-1 self-stretch md:gap-2">
					<p
						class="font-matter-sq text-xl font-normal leading-[150%] tracking-[-0.4px] text-primary md:text-2xl md:leading-9"
					>
						{{ t('title') }}
					</p>
					<p
						class="self-stretch text-sm font-normal leading-[175%] text-primary-light-3 md:text-base md:leading-[175%]"
					>
						{{ t('description', { condoName }) }}
					</p>
				</div>

				<div
					class="flex flex-col items-start gap-4 self-stretch md:grid md:grid-cols-2"
				>
					<div
						v-for="info in infos"
						:key="`condo-talk-to-specialist-info-${info.label}`"
						class="flex items-start gap-3 self-stretch"
					>
						<Component
							:is="info.icon"
							class="aspect-square size-5 text-primary md:size-6"
						/>

						<p class="text-sm font-medium leading-5 text-primary">
							{{ info.label }}
						</p>
					</div>
				</div>
			</div>
		</div>

		<div
			class="flex w-full flex-col gap-6 bg-secondary-dark-1 px-5 py-6 md:flex-row md:p-8"
		>
			<div>
				<slot />
			</div>

			<div
				class="flex items-center gap-3 self-stretch overflow-hidden max-md:order-first"
			>
				<BrokerAvatarSequence
					v-if="agents.length"
					:agents="agents"
					avatar-class="min-w-9 size-9 md:size-11 [&:not(:first-child)]:-ml-6"
				/>

				<div
					class="flex min-w-0 flex-1 flex-col items-start justify-center text-nowrap"
				>
					<p
						class="max-w-full truncate text-sm font-medium leading-5 text-primary md:text-base"
					>
						{{ agentsLabel }}
					</p>
					<p
						class="overflow-hidden text-xs font-normal leading-[150%] text-primary"
					>
						{{ t('condoName', { condoName }) }}
					</p>
				</div>
			</div>
		</div>
	</div>
</template>
